// Iter 145 — Temporary TalentLMS SSO debug terminal (Julian only).
//
// Hidden page (no dashboard tile): left pane embeds the TalentLMS OIDC
// start URL, right pane is a live terminal streaming every
// request/response between Conduit and TalentLMS with explicit HTTP
// status codes and response bodies (secrets redacted at write time).
// "Start SSO" ALSO fires a server-side round-trip probe: Conduit itself
// GETs the start_url and follows every redirect hop, so TalentLMS's
// actual answer (status, Location, body) is captured even though the
// iframe flow happens in the browser. Works while Julian masquerades.

import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  TerminalSquare, Play, ExternalLink, Trash2, Pause, ShieldAlert, Loader2, UserRound, Radar,
} from "lucide-react";

const LEVEL_TONE = {
  error: "text-red-400",
  warning: "text-amber-300",
  info: "text-emerald-300",
  debug: "text-slate-500",
};

function statusTone(sc) {
  if (!sc) return "bg-slate-700 text-slate-300";
  if (sc >= 500) return "bg-red-900 text-red-200";
  if (sc >= 400) return "bg-amber-900 text-amber-200";
  if (sc >= 300) return "bg-sky-900 text-sky-200";
  return "bg-emerald-900 text-emerald-200";
}

function fmtTs(ts) {
  try {
    const d = new Date(ts);
    return d.toLocaleTimeString("en-CA", { hour12: false }) + "." + String(d.getMilliseconds()).padStart(3, "0");
  } catch {
    return ts;
  }
}

function Section({ title, data }) {
  if (data === undefined || data === null || (typeof data === "object" && !Object.keys(data).length)) return null;
  return (
    <div className="mb-2">
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{title}</div>
      <pre className="mt-0.5 max-h-56 overflow-auto whitespace-pre-wrap break-all rounded bg-black/40 px-2 py-1 text-[11px] leading-relaxed text-lime-200">
        {typeof data === "string" ? data : JSON.stringify(data, null, 2)}
      </pre>
    </div>
  );
}

function TermRow({ row, idx }) {
  const [open, setOpen] = useState(false);
  const tone = LEVEL_TONE[row.level] || "text-slate-300";
  const ctx = row.context || {};
  const sc = ctx.status_code;
  const reqUrl = ctx.url || row.path || "";
  const method = ctx.method || row.method || "";
  const used = new Set(["method", "url", "path", "query", "req_headers", "request",
    "status_code", "duration_ms", "resp_headers", "resp_body", "location", "oidc_error", "error", "phase"]);
  const other = Object.fromEntries(Object.entries(ctx).filter(([k]) => !used.has(k)));
  return (
    <div className="border-b border-white/5" data-testid={`tlms-term-row-${idx}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-start gap-2 px-2 py-1 text-left hover:bg-white/5"
        data-testid={`tlms-term-row-toggle-${idx}`}
      >
        <span className="shrink-0 text-slate-600">{fmtTs(row.ts)}</span>
        <span className={`shrink-0 w-12 uppercase ${tone}`}>{row.level}</span>
        <span className={`shrink-0 w-12 rounded px-1 text-center font-bold ${sc ? statusTone(sc) : "text-slate-700"}`}>
          {sc || "—"}
        </span>
        <span className="shrink-0 w-28 truncate text-cyan-400">{row.action}</span>
        <span className={`min-w-0 flex-1 break-all ${row.status === "error" ? "text-red-300" : row.status === "denied" ? "text-amber-200" : "text-slate-200"}`}>
          {row.summary}
        </span>
      </button>
      {open && (
        <div className="bg-black/20 px-3 py-2" data-testid={`tlms-term-row-detail-${idx}`}>
          <Section title={`Request — ${method} ${reqUrl}`} data={{ headers: ctx.req_headers, query: ctx.query, body: ctx.request }} />
          <Section
            title={`Response — HTTP ${sc ?? "?"}${ctx.duration_ms != null ? ` · ${ctx.duration_ms}ms` : ""}`}
            data={{ location: ctx.location, oidc_error: ctx.oidc_error, error: ctx.error, headers: ctx.resp_headers, body: ctx.resp_body }}
          />
          <Section title="Context" data={{ user: row.user_email, client_id: row.client_id, ...other }} />
        </div>
      )}
    </div>
  );
}

function ProbeHop({ hop, idx }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-white/5" data-testid={`tlms-probe-hop-${idx}`}>
      <button type="button" onClick={() => setOpen((v) => !v)} className="flex w-full items-center gap-2 px-2 py-1 text-left hover:bg-white/5">
        <span className="shrink-0 text-slate-500">#{hop.hop}</span>
        <span className={`shrink-0 w-12 rounded px-1 text-center font-bold ${hop.error ? "bg-red-900 text-red-200" : statusTone(hop.status)}`}>
          {hop.error ? "ERR" : hop.status}
        </span>
        <span className={`shrink-0 rounded px-1 text-[10px] font-semibold uppercase ${hop.is_conduit ? "bg-emerald-950 text-emerald-300" : "bg-purple-950 text-purple-300"}`}>
          {hop.is_conduit ? "conduit" : "peer"}
        </span>
        <span className="min-w-0 flex-1 truncate text-slate-200">{hop.url}</span>
      </button>
      {open && (
        <div className="bg-black/20 px-3 py-2">
          <Section title="Hop detail" data={{
            error: hop.error, location: hop.location, content_type: hop.content_type,
            oidc_error: hop.oidc_error, peer_message: hop.peer_message, note: hop.note,
            cookie_sent: hop.cookie_sent, body_bytes: hop.body_bytes,
          }} />
          <Section title="Body preview" data={hop.body_preview} />
        </div>
      )}
    </div>
  );
}

export default function TlmsDebugTerminal() {
  const [access, setAccess] = useState(null);
  const [launch, setLaunch] = useState(null);
  const [probe, setProbe] = useState(null);
  const [busy, setBusy] = useState(false);
  const [iframeOn, setIframeOn] = useState(false);
  const [rows, setRows] = useState([]);
  const [live, setLive] = useState(true);
  const cursorRef = useRef(new Date(Date.now() - 60_000).toISOString());
  const liveRef = useRef(true);
  const termRef = useRef(null);

  useEffect(() => {
    api.get("/tlms-debug/access")
      .then(({ data }) => setAccess(data))
      .catch(() => setAccess({ allowed: false }));
  }, []);

  const poll = useCallback(async () => {
    if (!liveRef.current) return;
    try {
      const { data } = await api.get("/tlms-debug/stream", { params: { since: cursorRef.current } });
      if (data.rows?.length) {
        cursorRef.current = data.rows[data.rows.length - 1].ts;
        setRows((prev) => [...prev, ...data.rows].slice(-1000));
      }
    } catch { /* keep polling */ }
  }, []);

  useEffect(() => {
    if (!access?.allowed) return undefined;
    const t = setInterval(poll, 2000);
    return () => clearInterval(t);
  }, [access, poll]);

  useEffect(() => {
    const el = termRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [rows]);

  const runProbe = async () => {
    const { data } = await api.post("/tlms-debug/probe");
    setProbe(data);
  };

  const doLaunch = async () => {
    setBusy(true);
    setProbe(null);
    try {
      const { data } = await api.post("/tlms-debug/launch");
      setLaunch(data);
      setIframeOn(true);
      if (!data.can_access_app) toast.warning("Heads-up: the normal app launch would be DENIED for this user (still tracing).");
      await runProbe();   // server-side round trip so start_url's answer is captured
    } catch (e) {
      toast.error(e.response?.data?.detail || "Launch failed");
    } finally {
      setBusy(false);
    }
  };

  const doProbeOnly = async () => {
    setBusy(true);
    try {
      await runProbe();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Probe failed");
    } finally {
      setBusy(false);
    }
  };

  if (access === null) {
    return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  }
  if (!access.allowed) {
    return (
      <div className="mx-auto mt-16 max-w-md rounded-lg border border-slate-200 bg-white p-8 text-center" data-testid="tlms-debug-denied">
        <ShieldAlert className="mx-auto h-10 w-10 text-rose-500" />
        <div className="mt-3 text-lg font-semibold text-slate-800">Restricted debug tool</div>
        <p className="mt-1 text-sm text-slate-500">This temporary SSO debugger is only available to Julian.</p>
      </div>
    );
  }

  const verdictTone = probe?.ok ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-800";

  return (
    <div className="space-y-3" data-testid="tlms-debug-page">
      <div className="flex flex-wrap items-center gap-3">
        <TerminalSquare className="h-6 w-6 text-slate-700" />
        <div>
          <h1 className="text-lg font-semibold text-slate-900">TalentLMS SSO Debug Terminal</h1>
          <p className="text-xs text-slate-500">OIDC client “TalentLMS” · live Conduit ⇄ TalentLMS wire traffic with HTTP codes + responses (secrets redacted).</p>
        </div>
        <span className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700" data-testid="tlms-identity-chip">
          <UserRound className="h-3.5 w-3.5" />
          {access.effective_email}
          {access.masqueraded_by && (
            <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800">
              masquerade by {access.masqueraded_by.name}
            </span>
          )}
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" onClick={doLaunch} disabled={busy} data-testid="tlms-launch-btn">
          {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Play className="mr-1 h-4 w-4" />}
          {launch ? "Relaunch SSO" : "Start SSO"}
        </Button>
        <Button size="sm" variant="outline" onClick={doProbeOnly} disabled={busy} data-testid="tlms-probe-btn">
          <Radar className="mr-1 h-4 w-4" /> Server probe only
        </Button>
        {launch?.start_url && (
          <Button size="sm" variant="outline" asChild data-testid="tlms-open-tab-btn">
            <a href={launch.start_url} target="_blank" rel="noreferrer">
              <ExternalLink className="mr-1 h-4 w-4" /> Open in new tab
            </a>
          </Button>
        )}
        <Button size="sm" variant="outline" onClick={() => { liveRef.current = !live; setLive(!live); }} data-testid="tlms-pause-btn">
          {live ? <><Pause className="mr-1 h-4 w-4" /> Pause</> : <><Play className="mr-1 h-4 w-4" /> Resume</>}
        </Button>
        <Button size="sm" variant="outline" onClick={() => setRows([])} data-testid="tlms-clear-btn">
          <Trash2 className="mr-1 h-4 w-4" /> Clear
        </Button>
        {launch && (
          <span className="text-xs text-slate-500" data-testid="tlms-launch-meta">
            OIDC client {launch.client_name} ({launch.client_id}) · {launch.client_active ? "active" : "inactive"}
            · normal access: {launch.can_access_app ? "allowed" : "DENIED"}
          </span>
        )}
      </div>

      {probe && (
        <div className="overflow-hidden rounded-lg border border-slate-800 bg-[#0a0f14]" data-testid="tlms-probe-panel">
          <div className="flex flex-wrap items-center gap-2 border-b border-white/10 px-3 py-1.5 text-xs">
            <span className="font-semibold uppercase tracking-wide text-slate-400">Server round-trip probe</span>
            <span className={`rounded px-1.5 py-0.5 font-bold uppercase ${verdictTone}`} data-testid="tlms-probe-verdict">{probe.verdict}</span>
            <span className="text-slate-500">{probe.hops?.length || 0} hop(s) from</span>
            <span className="truncate font-mono text-[11px] text-slate-300">{probe.start_url}</span>
            {probe.peer_message && <span className="w-full text-amber-300" data-testid="tlms-probe-peer-msg">TalentLMS says: {probe.peer_message}</span>}
          </div>
          <div className="max-h-56 overflow-auto font-mono text-[12px] leading-snug">
            {(probe.hops || []).map((h, i) => <ProbeHop key={i} hop={h} idx={i} />)}
          </div>
        </div>
      )}

      <div className="grid gap-3 xl:grid-cols-2">
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
          <div className="border-b border-slate-100 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">TalentLMS (browser iframe)</div>
          {iframeOn && launch?.start_url ? (
            <iframe
              title="TalentLMS SSO"
              src={launch.start_url}
              className="h-[62vh] w-full"
              data-testid="tlms-iframe"
            />
          ) : (
            <div className="flex h-[62vh] items-center justify-center text-sm text-slate-400" data-testid="tlms-iframe-placeholder">
              Press “Start SSO” to load TalentLMS and begin the OIDC dance.
            </div>
          )}
        </div>

        <div className="overflow-hidden rounded-lg border border-slate-800 bg-[#0a0f14]">
          <div className="flex items-center gap-2 border-b border-white/10 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400">
            <span className={`h-2 w-2 rounded-full ${live ? "bg-emerald-400 animate-pulse" : "bg-slate-500"}`} />
            Wire terminal — {rows.length} events {live ? "(live)" : "(paused)"} · click a row for full request/response
          </div>
          <div ref={termRef} className="h-[62vh] overflow-auto font-mono text-[12px] leading-snug" data-testid="tlms-terminal">
            {rows.length === 0 ? (
              <div className="px-3 py-4 text-slate-600">Waiting for traffic… launch the SSO to see requests/responses appear here.</div>
            ) : (
              rows.map((r, i) => <TermRow key={`${r.ts}-${i}`} row={r} idx={i} />)
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
